import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { appRoutes } from '../../shared/constants/routes.constants';

import { AccessTokenMapper, UserMapper } from '../api/account.mapper';
import { AdminAccountController } from '../api/controllers/admin-account/admin-account.controller';
import { SuperAdminAccountController } from '../api/controllers/super-admin-account/super-admin-account.controller';
import { INewAccessToken, INewAccessTokenPayload, INewAccessTokenResponseDTO } from '../interfaces/new-access-token.interface';
import { IUser, IUserDTO } from '../interfaces/user.interface';
import { TokenService } from './token.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private _currentUserSubject$: BehaviorSubject<IUser> = new BehaviorSubject<IUser>(null);

  readonly loginRoute = appRoutes.LOGIN;

  constructor(
    private accountController: AdminAccountController,
    private superAdminAccountController: SuperAdminAccountController,
    private tokenService: TokenService,
  ) {}

  setUser(): void {
    if (this.getCurrentUserValue()) {
      return;
    }

    this.superAdminAccountController.getMe().subscribe({
      next: (response: IUserDTO) => {
        const user = UserMapper.fromDTO(response);
        this._currentUserSubject$.next(user);
      },
      error: (error) => {
        console.error('Get user error:', error);
        this._currentUserSubject$.next(null);
      },
    });
  }

  private updateUserWithoutLoading(): void {
    this.superAdminAccountController.getMe().subscribe({
      next: (response: IUserDTO) => {
        const user = UserMapper.fromDTO(response);
        this._currentUserSubject$.next(user);
      },
      error: (error) => {
        console.error('Get user error:', error);
        this._currentUserSubject$.next(null);
      },
    });
  }

  getCurrentUser(): Observable<IUser> {
    return this._currentUserSubject$.asObservable();
  }

  getCurrentUserValue(): IUser {
    return this._currentUserSubject$.getValue();
  }

  clearUser(): void {
    this._currentUserSubject$.next(null);
  }

  refreshCurrentUser(): void {
    this.clearUser();
    this.updateUserWithoutLoading();
  }

  refreshAccessToken(payload: INewAccessTokenPayload): Promise<INewAccessToken> {
    return new Promise<INewAccessToken>((resolve) => {
      this.accountController.getNewAccessToken(payload).subscribe({
        next: (response: INewAccessTokenResponseDTO) => {
          const newTokens = AccessTokenMapper.mapNewAccessTokenDTOToINewAccessToken(response);
          this.tokenService.setCurrentAccessToken(newTokens.accessToken);
          this.tokenService.setCurrentRefreshToken(newTokens.refreshToken);
          resolve(newTokens);
        },
        error: () => {
          resolve(null);
        },
      });
    });
  }
}
